import React, { useEffect, useState } from 'react';
import { SharedModal } from '../SharedModal';
import {
  saveColumnCondition,
  type CrmColumnCondition,
  type CrmColumnKind,
} from '../../services/CrmAutomationService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import './crmFunnel.css';

export const CONDITION_MAX_CHARS = 4000;

const KIND_OPTIONS: Array<{ value: CrmColumnKind | ''; label: string }> = [
  { value: '', label: 'Não definido' },
  { value: 'entrada', label: 'Entrada: onde novos contatos chegam' },
  { value: 'etapa', label: 'Etapa' },
  { value: 'ganho', label: 'Ganho: venda fechada, final' },
  { value: 'perdido', label: 'Perdido: final' },
];

const PLACEHOLDER =
  'Ex.: O cliente pediu um orçamento ou perguntou valores, e já informou o que precisa (produto, quantidade ou data).';

function backendMessage(error: unknown): string {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  if (data?.message?.trim()) return data.message.trim();
  return apiErrorMessage(error, 'Não foi possível salvar a condição.');
}

export interface ColumnConditionModalProps {
  open: boolean;
  sectorId: number;
  columnId: number;
  columnName: string;
  /** Condição atual (de listColumnConditions); null quando a coluna ainda não tem nenhuma. */
  condition: CrmColumnCondition | null;
  onClose: () => void;
  onSaved: (saved: CrmColumnCondition) => void;
}

const ColumnConditionModal: React.FC<ColumnConditionModalProps> = ({
  open,
  sectorId,
  columnId,
  columnName,
  condition,
  onClose,
  onSaved,
}) => {
  const [text, setText] = useState('');
  const [kind, setKind] = useState<CrmColumnKind | ''>('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setText(condition?.condition ?? '');
    setKind((condition?.kind as CrmColumnKind | null) ?? '');
    setEnabled(condition ? (condition.ruleId == null ? true : condition.enabled) : true);
    setError(null);
    setSaving(false);
  }, [open, condition]);

  const isShadow = !!condition && !condition.live && !!condition.condition?.trim();
  const extra = condition?.extraRules ?? 0;
  const tooLong = text.length > CONDITION_MAX_CHARS;

  const handleSave = async () => {
    if (tooLong) {
      setError(`A condição pode ter no máximo ${CONDITION_MAX_CHARS} caracteres.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveColumnCondition(sectorId, columnId, {
        condition: text.trim(),
        kind: kind || null,
        enabled,
      });
      onSaved(saved);
    } catch (e) {
      setError(backendMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SharedModal
      open={open}
      onClose={onClose}
      title={`Condição da coluna — ${columnName}`}
      size="lg"
      className="crm-automation-modal crm-condition-modal"
      overlayClassName="crm-automation-modal-overlay"
    >
      <div className="crm-automation-modal__form">
        {isShadow && (
          <p className="crm-funnel-notice crm-funnel-notice--warn" role="note">
            Esta é uma regra antiga: hoje a IA só registra sugestões (modo sombra). Ao salvar aqui, a movimentação
            automática passa a valer.
          </p>
        )}
        {extra > 0 && (
          <p className="crm-funnel-notice" role="note">
            +{extra} regra(s) antiga(s) desta coluna também são consideradas.
          </p>
        )}

        <div className="crm-automation-modal__form-group">
          <label className="crm-automation-modal__label" htmlFor="crm-condition-kind">Tipo</label>
          <select
            id="crm-condition-kind"
            className="crm-condition-modal__select"
            value={kind}
            onChange={(e) => setKind(e.target.value as CrmColumnKind | '')}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value || 'none'} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="crm-automation-modal__form-group">
          <label className="crm-automation-modal__label" htmlFor="crm-condition-text">
            Quando um contato pertence a esta coluna?
          </label>
          <div className="crm-automation-modal__field-with-optimize">
            <textarea
              id="crm-condition-text"
              className="crm-automation-modal__field-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder={PLACEHOLDER}
            />
          </div>
          <div className={`crm-condition-modal__counter ${tooLong ? 'is-over' : ''}`}>
            {text.length}/{CONDITION_MAX_CHARS}
          </div>
        </div>

        <div className="crm-automation-modal__toggle-row">
          <div>
            <div className="crm-automation-modal__label" id="crm-condition-enabled-label">
              A IA pode mover cards para esta coluna
            </div>
            <p className="crm-automation-modal__toggle-caption">
              Desligado, a condição fica guardada mas a IA não move cards para cá.
            </p>
          </div>
          <label className="crm-automation-modal__switch">
            <input
              type="checkbox"
              aria-labelledby="crm-condition-enabled-label"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="crm-automation-modal__switch-slider" />
          </label>
        </div>

        <details className="crm-condition-modal__help">
          <summary>Como a IA decide</summary>
          <ul>
            <li>Só move com um trecho literal da conversa ou um evento da agenda como evidência.</li>
            <li>Ganho e Perdido exigem confirmação explícita, e a IA nunca tira um card dessas colunas.</li>
            <li>Depois que alguém arrasta um card, a IA espera evidência nova (até 7 dias).</li>
            <li>Toda mudança fica registrada no card.</li>
          </ul>
        </details>

        {error && <div className="crm-funnel-error" role="alert">{error}</div>}

        <div className="crm-automation-modal__actions">
          <button
            type="button"
            className="crm-automation-modal__btn crm-automation-modal__btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="crm-automation-modal__btn crm-automation-modal__btn--primary"
            onClick={() => void handleSave()}
            disabled={saving || tooLong}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </SharedModal>
  );
};

export default ColumnConditionModal;
